import PropTypes from "prop-types";
import { Loader } from "@mantine/core";
import { Navigate } from "react-router-dom";
import useAuthStore from "../../../stores/authStore";
import { useCurrentUserQuery } from "../../../hooks/queries/useAuthQueries";
import UnauthorizedPage from "../../ui/CustomUnauthorized/CustomUnauthorized";
import { contactSupport } from "../../../utils/supportUtils";

const isAuthError = (error) => {
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

export default function AppInitializingGuard({ children }) {
  const { isAuthenticated, clearAuth } = useAuthStore();
  const { isLoading, isError, error, refetch } = useCurrentUserQuery({
    enabled: isAuthenticated,
  });

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  if (isError) {
    if (isAuthError(error)) {
      clearAuth();
      return <Navigate to="/auth/login" replace />;
    }

    return (
      <UnauthorizedPage
        leftTitle="Something Went Wrong"
        leftMessage="We're sorry for the inconvenience."
        rightTitle="Unable to load the application"
        rightMessage="We're sorry; something went wrong. Please try again. If the problem persists, contact our support team."
        actions={[
          { label: "Try Again", onClick: refetch, variant: "filled" },
          {
            label: "Contact Support",
            onClick: () => contactSupport("Application Load Error"),
            variant: "outline",
          },
        ]}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gray-50">
        <Loader color="blue" />
      </div>
    );
  }

  return children;
}

AppInitializingGuard.propTypes = {
  children: PropTypes.node.isRequired,
};
